import { Box, Chip, IconButton, Tooltip, Typography } from "@mui/material";
import { Link, PaginationPayload, useGetList } from "react-admin";
import { Student } from "../../../providers/studentsProvider";
import { cardTitleStyle, overviewCardStyle } from "../../../misc/backofficeTheme";
import { INVOICE_STATUS_LABEL_AND_COLOR } from "../../../backoffice.utils";
import { InvoiceIcon } from "../../../icons/InvoiceIcon";
import { Spinner } from "../../../misc/Spinner";
import { Row } from "../../../misc/Row";
import { getInvoiceStateLabel } from "../../../fields/InvoiceStateField";
import { useStudentLabelPalette } from "../../../hooks/useStudentLabelPalette";

type InvoiceStateKey = keyof typeof INVOICE_STATUS_LABEL_AND_COLOR;

export function LatestInvoicesCard({ student }: { student: Student }) {
  const studentLabelColors = useStudentLabelPalette();
  const { data } = useGetList("b2cInvoices", {
    filter: { studentId: student.id },
    pagination: { perPage: 5 } as PaginationPayload,
    sort: { field: "name", order: "ASC" },
  });

  return (
    <Box
      sx={{
        ...overviewCardStyle,
        padding: "20px 30px",
        position: "relative",
      }}
    >
      <Typography sx={cardTitleStyle}>Letzte Rechnungen</Typography>
      {!data && (
        <div className="centered">
          <Spinner style={{ margin: "20px" }} />
        </div>
      )}
      {data &&
        data?.map((invoice) => {
          const label = getInvoiceStateLabel(invoice.payload);
          const status = INVOICE_STATUS_LABEL_AND_COLOR[invoice.status as InvoiceStateKey];
          const colors = status ? studentLabelColors[status.labelColor] : undefined;
          return (
            <Box key={invoice.id} sx={{ display: "flex", alignItems: "center", gap: 2, marginBottom: 2 }}>
              <IconButton sx={{ backgroundColor: "action.hover" }}>
                <InvoiceIcon />
              </IconButton>
              <Row justifyContent="space-between" alignItems="center" flexWrap="wrap" width="100%">
                <Box sx={{ flex: 3 }}>
                  <Typography sx={{ fontSize: 14 }}>{invoice.nr}</Typography>
                  <Typography
                    sx={{
                      fontSize: 12,
                      color: "text.secondary",
                      whiteSpace: "nowrap",
                      overflow: "hidden",
                      textOverflow: "ellipsis",
                      width: 170,
                    }}
                  >
                    {invoice.payload.positions[0]?.description}
                  </Typography>
                </Box>
                <Box>
                  <Tooltip title={label} placement="bottom-end">
                    <Chip
                      sx={{
                        backgroundColor: colors?.background,
                        color: colors?.text,
                        whiteSpace: "nowrap",
                        overflow: "hidden",
                        textOverflow: "ellipsis",
                        width: 65,
                        height: 20,
                        fontSize: "10px",
                        fontWeight: "500",
                        lineHeight: 2,
                      }}
                      label={label}
                    />
                  </Tooltip>
                </Box>
              </Row>
            </Box>
          );
        })}
      {data && data.length === 0 && (
        <Typography sx={{ color: "text.secondary", marginTop: -1, fontSize: 14 }}>Noch keine Rechnung</Typography>
      )}
      {data && data.length > 0 && (
        <Link to={`/students/${student.id}/invoices`} variant="body2">
          Alle Rechnungen
        </Link>
      )}
    </Box>
  );
}
